
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import * as echarts from "echarts";

export interface LineDataType {
  lineName: string; // 图表名称
  xData: any[]; // x轴数据
  yData: any[]; // y轴数据
  legendList: any[];
  type: number; // 类型
  xAxisData: any[];
  referenceTarget?: number; // 参考目标值
}

@Component
export class StackedBarChart extends Vue {
  @Prop({ default: () => ({ xData:[], yData:[], lineName:'', legendList: ["已抽奖人数", "可抽奖人数", "参考目标（基于上期活动）"], type: 1, xAxisData: [],referenceTarget: 0 }) }) params!: LineDataType;
  @Prop({ default: false }) simple!: boolean;

  chart: any = null;
  legendColorList = ["#1A5EF9", "#21CCFF", "#F44336"];

  @Watch("params", { deep: true, immediate: true })
  onParamsChange() {
    this.$nextTick(() => {
      const dom = this.$refs.StackedBarChartRef as HTMLElement;
      if (dom) dom.innerHTML = '';
      console.log('StackedBarChart----', this.params)
      this.initChart();
    });
  }

  mounted() {
    this.initChart();
  }

  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  }

  initChart() {
    const dom = this.$refs.StackedBarChartRef as HTMLElement;
    if (!dom) return;

    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }

    this.chart = echarts.init(dom);
    
    this.chart.setOption(this.normalOption(), true);
  }

  normalOption() {
    const barWidth = 30
    // 1. 计算所有柱子的总值（已抽奖 + 可抽奖）
    const stackData = this.params.xData.map((item, i) => (item || 0) + (this.params.yData[i] || 0))
    const maxStack = stackData.length ? Math.max(...stackData) : 0
    const reference = this.params.referenceTarget || 0
    // 真实最大值（柱子最大值 和 参考线取大）
    const realMax = Math.max(maxStack, reference)
    let yMax, yInterval
    // 2. 智能判断：≤10 用小刻度，>10 用20间隔
    if (realMax <= 10) {
      // 情况1：小于等于10 → 间隔2
      yMax = 10
      yInterval = 2
    } else if (realMax <= 100) {
      // 情况2：10 ~ 100 → 间隔20，最小100
      yMax = 100
      yInterval = 20
    } else {
      // 情况3：大于100 → 间隔50，向上取整到50的倍数
      yMax = Math.ceil(realMax / 50) * 50
      yInterval = 50
    }
    return {
      title: {
        text: this.params.lineName,
        x: "center",
        y: 0,
        textStyle: {
          color: "#1D2129",
          fontSize: 12,
          fontWeight: "normal",
        },
      },
      backgroundColor: "#fff",
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any) => {
          const category = params[0].name;
          let drawn = 0;    // 已抽奖
          let remain = 0;   // 剩余可抽奖

          params.forEach((p: any) => {
            // 第 0 个系列 = 已抽奖人数
            if (p.seriesIndex === 0) {
              drawn = p.value;
            }
            // 第 1 个系列 = 可抽奖人数
            if (p.seriesIndex === 1) {
              remain = p.value;
            }
          });

          // 总可抽奖 = 已抽奖 + 剩余可抽奖
          // const drawable = drawn + remain;

          return `
            <div style="text-align: left;">
              <div>${category}</div>
              <div>${this.params.type === 1 ? '已抽奖人数：' : '已抽奖次数：'}${drawn}${this.params.type === 1 ? '人' : '次'}</div>
              <div>${this.params.type === 1 ? '可抽奖人数：' : '可抽奖次数：'}${remain}${this.params.type === 1 ? '人' : '次'}</div>
            </div>
          `;
        },
      },
      legend: {
        data: this.params.legendList,
        textStyle: { color: "#4E5969" },
        top: "4%",
        right: "10%",
      },
      grid: {
        containLabel: true,
        left: 6,
        right: 6,
        top: 20,
        bottom: 10,
      },
      xAxis: {
        data: this.params.xAxisData,
        axisLine: { lineStyle: { color: "#C9CDD4"} },
        axisTick: { show: false },
        axisLabel: { color: "#4E5969", fontSize: 12 },
      },
      yAxis: {
        splitLine: { lineStyle: { color: "#F2F3F5",type: 'dashed' } },
        axisLine: { lineStyle: { color: "#C9CDD4", } },
        axisLabel: {
          // formatter: "{value} " + (this.params.type === 1 ? '人' : '次'),
          formatter: "{value} ",
          color: "#4E5969",
        },
        min: 0,
        max: yMax, // 动态最大值
        interval: yInterval, // 等间隔
      },
      series: [
        // 1. 已抽奖人数（底部蓝色柱）
        {
          // name: "已抽奖" + (this.params.type === 1 ? '人数' : '次数'),
          type: "bar",
          barWidth: barWidth,
          stack: "total",
          itemStyle: {
            normal: {
              color: this.legendColorList[0],
            },
          },
          label: {
            show: true,
            // position: "inside",
            position: "top",
            color: "#1D2129",
            fontSize: 12,
            // formatter: (params: any) => `${params.value}${this.params.type === 1 ? '人' : '次'}`,
            formatter: (params: any) => `${params.value}`,
          },
          data: this.params.xData,
        },
        // 2. 可抽奖差额（上方青色柱）
        {
          // name: "可抽奖" + (this.params.type === 1 ? '人数' : '次数'),
          type: "bar",
          barWidth: barWidth,
          stack: "total",
          itemStyle: {
            normal: {
              color: this.legendColorList[1],
            },
          },
          label: {
            show: true,
            position: "top",
            color: "#1D2129",
            fontSize: 12,
            formatter: (params: any) => {
              const total = params.value;
              // return `${total}${this.params.type === 1 ? '人' : '次'}`;
              return `${total}`;
            },
          },
          data: this.params.yData,
        },
        // 3. 参考目标（置顶显示，不被遮挡）
        {
          // name: "参考目标（基于上期活动）",
          type: "custom",
          z: 999, // 置顶
          renderItem: (params, api) => {
            // 只在第 0 个柱子（合计）画
            if (params.dataIndex !== 0) return null;

            const value = this.params.referenceTarget;
            const barWidth = 30;
            const x = api.coord([0, value])[0];
            const y = api.coord([0, value])[1];

            return {
              type: "group",
              children: [
                // 横线
                {
                  type: "line",
                  shape: {
                    x1: x - barWidth / 2,
                    y1: y,
                    x2: x + barWidth / 2,
                    y2: y,
                  },
                  style: {
                    stroke: "#F44336",
                    lineWidth: 2,
                  },
                },
                {
                  type: "text",
                  position: "left",
                  style: {
                    text: `${this.params.referenceTarget}`,
                    x: x + barWidth / 2 - 24, // 基准文字左右位置
                    y: y - 15, // 基准文字上下位置
                    fontSize: 12,
                    fill: "#1D2129", // 文字颜色
                  },
                },
              ],
            };
          },
          data: [0],
        }
      ],
    };
  }
}

export default StackedBarChart;
